import React, { useState } from 'react';
import { PieChart, RefreshCw, Users, CheckCircle, X, TrendingUp, Clock, Zap, CheckSquare, AlertTriangle, ChevronRight } from 'lucide-react';
import { safeAsync, emptyMsg, btnSm, neuShadow, neuShadowSm, neuShadowInset, cardWrap } from './crmShared';

export default function AnalyticsTab({ analytics, setAnalytics, contacts }) {
  const [expandedSections, setExpandedSections] = useState(new Set(['Key Metrics']));

  const toggleSection = (sectionName) => {
    const newSet = new Set(expandedSections);
    if (newSet.has(sectionName)) newSet.delete(sectionName);
    else newSet.add(sectionName);
    setExpandedSections(newSet);
  };

  const AccordionSection = ({ title, icon: Icon, isOpen, onToggle, badge, children, defaultOpen = false }) => {
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const open = isOpen !== undefined ? isOpen : internalOpen;
    const handleClick = onToggle || (() => setInternalOpen(!internalOpen));

    return (
      <div style={{ ...cardWrap, marginBottom: 16 }}>
        <button
          onClick={handleClick}
          style={{
            width: '100%', padding: '16px', border: 'none', background: '#F8F9FA',
            display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
            fontSize: 13, fontWeight: 700, color: '#212529',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#f0f2f7'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#F8F9FA'}
        >
          <ChevronRight
            size={18}
            color="#2E8B98"
            style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}
          />
          <Icon size={16} color="#2E8B98" />
          <span>{title}</span>
          {badge && <span style={{ fontSize: 11, background: '#2E8B98', color: '#fff', padding: '2px 8px', borderRadius: 10, marginLeft: 'auto' }}>{badge}</span>}
        </button>

        {open && (
          <div style={{
            maxHeight: '2000px',
            overflow: 'hidden',
            transition: 'max-height 0.3s ease',
            padding: 20,
            borderTop: '1px solid #e0e5ec'
          }}>
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <PieChart size={18} color="#2E8B98" /> Analytics Dashboard
        </h3>
        <button onClick={safeAsync(async () => { const r = await window.zignagenixAPI.getCRMAnalytics({ days: 30 }); setAnalytics(r); })} style={btnSm}><RefreshCw size={12} /> Refresh</button>
      </div>

      {!analytics ? (
        <div style={cardWrap}>
          <div style={{ ...emptyMsg }}>Loading analytics… Click Refresh if data doesn't appear.</div>
        </div>
      ) : (
        <>
          {/* Key Metrics */}
          <AccordionSection
            title="Key Metrics"
            icon={Users}
            isOpen={expandedSections.has('Key Metrics')}
            onToggle={() => toggleSection('Key Metrics')}
            defaultOpen={true}
          >
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[
                { label: 'Total Active', value: analytics.totalActive, color: '#2E8B98', icon: Users },
                { label: 'Won', value: analytics.wonCount, color: '#62BB46', icon: CheckCircle },
                { label: 'Lost', value: analytics.lostCount, color: '#DC3545', icon: X },
                { label: 'Win Rate', value: analytics.totalActive > 0 ? `${Math.round(analytics.wonCount / Math.max(1, analytics.wonCount + analytics.lostCount) * 100)}%` : '—', color: '#256F7A', icon: TrendingUp },
                { label: 'Avg Days in Pipeline', value: `${analytics.avgDaysInPipeline}d`, color: '#F26522', icon: Clock },
                { label: 'Avg Response Time', value: `${analytics.avgResponseTime}h`, color: '#2E8B98', icon: Zap },
                { label: 'Tasks Completed', value: `${analytics.tasksCompleted}/${analytics.tasksTotal}`, color: '#4FA038', icon: CheckSquare },
                { label: 'Tasks Overdue', value: analytics.tasksOverdue, color: '#DC3545', icon: AlertTriangle },
              ].map((kpi, i) => {
                const I = kpi.icon;
                return (
                  <div key={i} style={{ background: '#F8F9FA', borderRadius: 14, boxShadow: neuShadow, padding: '14px 18px', minWidth: 130, flex: '1 1 130px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <I size={14} color={kpi.color} />
                      <span style={{ fontSize: 10, color: '#6C757D', fontWeight: 600 }}>{kpi.label}</span>
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: kpi.color }}>{kpi.value}</div>
                  </div>
                );
              })}
            </div>
          </AccordionSection>

          {/* Pipeline Funnel */}
          <AccordionSection
            title="Pipeline Funnel"
            icon={TrendingUp}
            isOpen={expandedSections.has('Pipeline Funnel')}
            onToggle={() => toggleSection('Pipeline Funnel')}
          >
            <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 120 }}>
              {(analytics.funnel || []).map((s, i) => {
                const firstStageCount = (analytics.funnel && analytics.funnel.length > 0) ? analytics.funnel[0].count : 1;
                const pct = firstStageCount > 0 ? (s.count / firstStageCount) * 100 : 0;
                const colors = ['#2E8B98', '#2E8B98', '#256F7A', '#a78bfa', '#c084fc', '#d946ef', '#2E8B98', '#f43f5e', '#F26522', '#62BB46', '#4FA038', '#2E8B98', '#06b6d4', '#14b8a6'];
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: colors[i % colors.length] }}>{s.count}</span>
                    <div style={{ width: '100%', height: `${Math.max(pct, 5)}%`, background: colors[i % colors.length], borderRadius: 6, boxShadow: neuShadowSm, minHeight: 4 }} />
                    <span style={{ fontSize: 8, color: '#6C757D', textAlign: 'center', lineHeight: 1.1 }}>{(s.pipeline_stage || '').replace(/_/g, ' ')}</span>
                  </div>
                );
              })}
            </div>
          </AccordionSection>

          {/* Activity Trends */}
          <AccordionSection
            title="Activity Trends"
            icon={Clock}
            isOpen={expandedSections.has('Activity Trends')}
            onToggle={() => toggleSection('Activity Trends')}
          >
            <div style={{ display: 'flex', gap: 16 }}>
              {/* Activity Trend */}
              <div style={{ flex: 1 }}>
                <h5 style={{ fontSize: 12, fontWeight: 600, color: '#212529', margin: '0 0 12px 0' }}>Activity Trend ({analytics.days}d)</h5>
                <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 80 }}>
                  {(analytics.activityByDay || []).slice(-30).map((d, i) => {
                    const max = Math.max(...(analytics.activityByDay || []).map(x => x.count), 1);
                    return (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }} title={`${d.day}: ${d.count}`}>
                        <div style={{ width: '100%', height: `${(d.count / max) * 100}%`, background: '#2E8B98', borderRadius: 3, minHeight: 2, boxShadow: neuShadowSm }} />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* New Contacts Trend */}
              <div style={{ flex: 1 }}>
                <h5 style={{ fontSize: 12, fontWeight: 600, color: '#212529', margin: '0 0 12px 0' }}>New Contacts ({analytics.days}d)</h5>
                <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 80 }}>
                  {(analytics.newContactsByDay || []).slice(-30).map((d, i) => {
                    const max = Math.max(...(analytics.newContactsByDay || []).map(x => x.count), 1);
                    return (
                      <div key={i} style={{ flex: 1 }} title={`${d.day}: ${d.count}`}>
                        <div style={{ width: '100%', height: `${(d.count / max) * 100}%`, background: '#62BB46', borderRadius: 3, minHeight: 2, boxShadow: neuShadowSm }} />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </AccordionSection>

          {/* Lead Score Distribution */}
          <AccordionSection
            title="Lead Score Distribution"
            icon={PieChart}
            isOpen={expandedSections.has('Lead Score Distribution')}
            onToggle={() => toggleSection('Lead Score Distribution')}
          >
            <div>
              {(analytics.scoreDistribution || []).map(s => {
                const gc = { 'A+': '#4FA038', 'A': '#62BB46', 'B': '#2E8B98', 'C': '#F26522', 'D': '#F26522', 'F': '#DC3545' };
                return (
                  <div key={s.grade} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: gc[s.grade] || '#6C757D', width: 30 }}>{s.grade}</span>
                    <div style={{ flex: 1, background: '#e0e5ec', borderRadius: 4, height: 14, boxShadow: neuShadowInset }}>
                      <div style={{ width: `${Math.min(100, (s.count / Math.max(contacts.length, 1)) * 100)}%`, height: '100%', background: gc[s.grade], borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 4 }}>
                        <span style={{ fontSize: 9, color: '#fff', fontWeight: 700 }}>{s.count}</span>
                      </div>
                    </div>
                    <span style={{ fontSize: 10, color: '#6C757D', width: 50 }}>avg {Math.round(parseFloat(s.avg_prediction || 0))}%</span>
                  </div>
                );
              })}
            </div>
          </AccordionSection>

          {/* Team Performance */}
          <AccordionSection
            title="Team Performance"
            icon={Users}
            isOpen={expandedSections.has('Team Performance')}
            onToggle={() => toggleSection('Team Performance')}
          >
            <div>
              {(analytics.teamPerformance || []).map((tp, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#212529', width: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tp.display_name}</span>
                  <div style={{ flex: 1, background: '#e0e5ec', borderRadius: 4, height: 12, boxShadow: neuShadowInset }}>
                    <div style={{ width: `${Math.min(100, (tp.interactions / Math.max(...(analytics.teamPerformance || []).map(x => x.interactions), 1)) * 100)}%`, height: '100%', background: '#2E8B98', borderRadius: 4 }} />
                  </div>
                  <span style={{ fontSize: 10, color: '#6C757D', width: 80, textAlign: 'right' }}>{tp.interactions} acts / {tp.unique_contacts} contacts</span>
                </div>
              ))}
              {(analytics.teamPerformance || []).length === 0 && <div style={{ fontSize: 11, color: '#ADB5BD' }}>No team activity in this period.</div>}
            </div>
          </AccordionSection>
        </>
      )}
    </div>
  );
}
